import { ComponentFactoryResolver, ComponentRef, Type, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { loadProposalHistoryModalShellComponent } from './lazy-load-proposal-history-modal-shell';

let cachedShellComponent: Type<any> | null = null;

export interface ProposalHistoryModalShellBindings {
  loadAllSub: Subscription | null;
  reloadSub: Subscription | null;
  closeSub: Subscription | null;
  retrySub: Subscription | null;
}

export interface ProposalHistoryModalShellViewState {
  title: string;
  isMinimized: boolean;
  isLoading: boolean;
  hasRenderedContent: boolean;
  hasLoadError: boolean;
  loadAllChecked: boolean;
  skeletonCount: number;
  errorMessage: string;
  showRetry: boolean;
}

export async function mountProposalHistoryModalShell(params: {
  host: ViewContainerRef;
  componentFactoryResolver: ComponentFactoryResolver;
  onLoadAll: (checked: boolean) => void;
  onReload: () => void;
  onClose: () => void;
  onRetry: () => void;
}): Promise<{ ref: ComponentRef<any>; bindings: ProposalHistoryModalShellBindings }> {
  if (!cachedShellComponent) {
    cachedShellComponent = await loadProposalHistoryModalShellComponent();
  }

  const componentFactory = params.componentFactoryResolver.resolveComponentFactory(cachedShellComponent);
  params.host.clear();
  const ref = params.host.createComponent(componentFactory);
  const instance = ref.instance as any;

  const bindings: ProposalHistoryModalShellBindings = {
    loadAllSub: instance.loadAllChange?.subscribe?.((checked: boolean) => params.onLoadAll(checked)) || null,
    reloadSub: instance.reload?.subscribe?.(() => params.onReload()) || null,
    closeSub: instance.close?.subscribe?.(() => params.onClose()) || null,
    retrySub: instance.retry?.subscribe?.(() => params.onRetry()) || null,
  };

  return { ref, bindings };
}

export function syncProposalHistoryModalShellState(ref: ComponentRef<any> | null, state: ProposalHistoryModalShellViewState): void {
  if (!ref) {
    return;
  }
  const shellInstance = ref?.instance as any;
  if (!shellInstance) {
    return;
  }
  const isUnchanged =
    shellInstance.title === state.title &&
    shellInstance.isMinimized === state.isMinimized &&
    shellInstance.isLoading === state.isLoading &&
    shellInstance.hasRenderedContent === state.hasRenderedContent &&
    shellInstance.hasLoadError === state.hasLoadError &&
    shellInstance.loadAllChecked === state.loadAllChecked &&
    shellInstance.skeletonCount === state.skeletonCount &&
    shellInstance.errorMessage === state.errorMessage &&
    shellInstance.showRetry === state.showRetry;
  if (isUnchanged) {
    return;
  }
  shellInstance.title = state.title;
  shellInstance.isMinimized = state.isMinimized;
  shellInstance.isLoading = state.isLoading;
  shellInstance.hasRenderedContent = state.hasRenderedContent;
  shellInstance.hasLoadError = state.hasLoadError;
  shellInstance.loadAllChecked = state.loadAllChecked;
  shellInstance.skeletonCount = state.skeletonCount;
  shellInstance.errorMessage = state.errorMessage;
  shellInstance.showRetry = state.showRetry;
  ref.changeDetectorRef.detectChanges();
}

export function disposeProposalHistoryModalShellBindings(bindings: ProposalHistoryModalShellBindings | null): null {
  bindings?.loadAllSub?.unsubscribe();
  bindings?.reloadSub?.unsubscribe();
  bindings?.closeSub?.unsubscribe();
  bindings?.retrySub?.unsubscribe();
  return null;
}
